import { useCallback, useEffect, useMemo, useRef } from "react";

/** ===== Helpers de conteúdo ===== */
const isObj = (x) => x && typeof x === "object" && !Array.isArray(x);

function canon(v) {
  if (Array.isArray(v)) return v.map(canon); // arrays preservam ordem
  if (isObj(v)) {
    const out = {};
    const keys = Object.keys(v)
      .filter((k) => v[k] !== undefined)
      .sort();
    for (const k of keys) out[k] = canon(v[k]); // objetos ordenam keys
    return out;
  }
  return v;
}
export function deepEqualByContent(a, b) {
  try {
    return JSON.stringify(canon(a)) === JSON.stringify(canon(b));
  } catch {
    return String(a) === String(b);
  }
}

/** Diff top-level: devolve { campo: valorCompletoDoCampo } */
export function diffTopLevel(current, base) {
  const out = {};
  const keys = new Set([
    ...Object.keys(current || {}),
    ...Object.keys(base || {}),
  ]);
  for (const k of keys) {
    const cv = current ? current[k] : undefined;
    const bv = base ? base[k] : undefined;
    if (!deepEqualByContent(cv, bv)) out[k] = cv;
  }
  return out;
}

/** Aplica campos por inteiro */
export function applyDraftFields(form, changedFields) {
  const next =
    typeof structuredClone === "function"
      ? structuredClone(form)
      : JSON.parse(JSON.stringify(form));
  for (const k of Object.keys(changedFields || {})) next[k] = changedFields[k];
  return next;
}

/** ===== Storage ===== */
function checksum(str) {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = (h << 5) - h + str.charCodeAt(i);
    h |= 0;
  }
  return String(h);
}
function stableStringify(obj) {
  if (!isObj(obj)) return JSON.stringify(obj);
  const keys = Object.keys(obj).sort();
  const ordered = {};
  for (const k of keys) ordered[k] = JSON.parse(stableStringify(obj[k]));
  return JSON.stringify(ordered);
}

function makeKeys({ notice, appId, schemaVersion }) {
  const base = `draft:${notice}:${appId}:${schemaVersion}`;
  return {
    prev: `${base}:prev`,
    curr: `${base}:curr`,
  };
}

function readPayload(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const payload = JSON.parse(raw);
    const cp = { ...payload };
    const sig = cp.checksum;
    delete cp.checksum;
    if (checksum(stableStringify(cp)) !== String(sig)) return null;
    return payload;
  } catch {
    return null;
  }
}
function writePayload(key, payload) {
  const cp = { ...payload };
  const raw = stableStringify(cp);
  cp.checksum = checksum(raw);
  try {
    localStorage.setItem(key, JSON.stringify(cp));
  } catch {}
}
function del(key) {
  try {
    localStorage.removeItem(key);
  } catch {}
}

/** ===== Hook =====
 * Mantém dois rascunhos:
 *   - prev: rascunho anterior (só este é visível na toolbar)
 *   - curr: rascunho atual (sessão corrente), atualizado por autosave
 */
export function useLocalDrafts({
  notice,
  appId,
  schemaVersion = "v1",
  baseGetter, // () => application.content
  currentGetter, // () => formData
  ttlDays = 7,
}) {
  const keys = useMemo(
    () => makeKeys({ notice, appId, schemaVersion }),
    [notice, appId, schemaVersion]
  );
  const debounced = useRef(null);

  // Na entrada: se existir um "curr" antigo, promove para "prev" e limpa curr.
  useEffect(() => {
    if (!notice || !appId) return;
    const curr = readPayload(keys.curr);
    if (curr) {
      // dá sempre prioridade ao curr mais recente como "prev"
      const prev = {
        ts: curr.ts,
        ttl: curr.ttl,
        changed: curr.changed || {},
        schemaVersion,
      };
      writePayload(keys.prev, prev);
      del(keys.curr);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keys.prev, keys.curr, notice, appId]);

  const hasPrev = useCallback(() => {
    const p = readPayload(keys.prev);
    if (!p) return false;
    const expired = Date.now() - (p.ts || 0) > ttlDays * 24 * 60 * 60 * 1000;
    if (expired) {
      del(keys.prev);
      return false;
    }
    return Object.keys(p.changed || {}).length > 0;
  }, [keys.prev, ttlDays]);

  const getPrev = useCallback(() => {
    const p = readPayload(keys.prev);
    if (!p) return null;
    return p.changed || null;
  }, [keys.prev]);

  const clearPrev = useCallback(() => del(keys.prev), [keys.prev]);
  const clearCurr = useCallback(() => del(keys.curr), [keys.curr]);
  const clearAll = useCallback(() => {
    del(keys.prev);
    del(keys.curr);
  }, [keys.prev, keys.curr]);

  // Autosave → guarda no "curr" os campos alterados vs base
  const saveCurrNow = useCallback(() => {
    const base = baseGetter?.();
    const cur = currentGetter?.();
    if (!base || !cur) return;
    const changed = diffTopLevel(cur, base);
    if (!changed || Object.keys(changed).length === 0) return;
    writePayload(keys.curr, {
      ts: Date.now(),
      ttl: ttlDays * 24 * 60 * 60 * 1000,
      changed,
      schemaVersion,
    });
  }, [baseGetter, currentGetter, keys.curr, ttlDays, schemaVersion]);

  const scheduleCurrSave = useCallback(
    (delay = 1000) => {
      if (debounced.current) clearTimeout(debounced.current);
      debounced.current = setTimeout(saveCurrNow, delay);
    },
    [saveCurrNow]
  );

  useEffect(
    () => () => {
      if (debounced.current) clearTimeout(debounced.current);
    },
    []
  );

  // Fundir prev → curr (quando user decide restaurar)
  const mergePrevIntoCurr = useCallback(() => {
    const prev = readPayload(keys.prev)?.changed || {};
    if (!prev || Object.keys(prev).length === 0) return;
    const curr = readPayload(keys.curr)?.changed || {};
    const merged = { ...curr, ...prev }; // campos do prev sobrepõem
    writePayload(keys.curr, {
      ts: Date.now(),
      ttl: ttlDays * 24 * 60 * 60 * 1000,
      changed: merged,
      schemaVersion,
    });
  }, [keys.prev, keys.curr, ttlDays, schemaVersion]);

  return {
    hasPrev,
    getPrev, // { field: fullValue }
    clearPrev,
    clearCurr,
    clearAll,

    scheduleCurrSave,
    saveCurrNow,
    mergePrevIntoCurr,
  };
}
